import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';
import { DownloadIcon } from '@heroicons/react/outline';
import { Delete, DeleteForever, RestoreFromTrash } from '@mui/icons-material';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { GridColDef, GridSelectionModel, GridSortItem, GridSortModel, useGridApiRef } from '@mui/x-data-grid-pro';
import {
  downloadAsData,
  filteredStipType,
  getOptions,
  gridDateTimeFormatter,
  queryParams,
  withQueryParams,
} from '@shared/commonFunctions';
import { dataGridCol } from '@shared/components/grid/common';
import { gridRowActionsIconProps } from '@shared/components/grid/GridColActions';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { useUsers } from '@shared/hooks/useUsers';
import { GroupUser } from '@shared/interfaces';
import { useTable } from '@shared/hooks/useTable';
import { useStipTypes } from '@shared/hooks/useStipTypes';
import { SERVICES } from '@shared/constants/apiConfig';
import { colorTheme } from '@styles/muiTheme';
import { ALERT_TYPES, IContactFile, IFilesTableProps, IStip } from '../interfaces';
import { requestDeleteStipFiles, requestGetStip, requestRestoreStipFiles, requestUpdateStipType } from '../requests';
import { NContact } from '../../Contact/validation';
import { IAsset } from '../../Assets/interfaces';
import { ICoApplicant } from '../../Contact/components/CoApplicant/validation';
import { StipReviewModal } from './StipReviewModal';
import { getComparableFieldsForContactFile, getFieldCompareStatus, normalizeContactFileToStipReview } from './helper';

interface IStipTypeMap {
  [key: string]: string;
}

const defaultSort: GridSortItem[] = [{ field: 'isDeleted', sort: 'asc' }];
const FULL_CONFIDENCE_SCORE = 100;
const ZERO_CONFIDENCE_SCORE = 0;

interface ConfidenceScoreProps {
  score?: number;
  onClick?: () => void;
}

export const ConfidenceScore = ({ score, onClick }: ConfidenceScoreProps) => {
  const hasScore = score != null;
  const isGreen = hasScore && score > 75;

  return (
    <>
    {
      hasScore ? (
        <Box
          sx={{
            px: '0.3rem',
            py: '0.4rem',
            borderRadius: '999px',
            backgroundColor: isGreen ? colorTheme.palette.success.main : colorTheme.palette.error.main,
            minWidth: 52,
            textAlign: 'center',
          }}
          onClick={onClick}
      >
      <Typography
        variant="caption"
        sx={{ color: '#fff', fontWeight: 600, justifyContent: 'center' }}
      >
        {`${score}%`}
      </Typography>
    </Box>
      ) : 
      <Typography
        variant="caption"
        sx={{ color: '#000', fontWeight: 600 }}
      >
        {'-'}
      </Typography>
    }
    </>
  );
};

export const FilesTable = ({
  rows,
  reloadFiles,
  selectedFiles,
  handleSelectedFiles,
  dealId,
  assetId,
  isLoading,
}: IFilesTableProps) => {
  const dispatch = useDispatch();
  const { stipTypes } = useStipTypes();
  const { sortModel, setSortModel } = useTable({ sortModel: defaultSort.concat([{ field: 'stipType', sort: 'asc' }]) });
  const [deleteContactStipData, setDeleteContactStipData] = useState<IContactFile>();
  const [contactStipData, setContactStipData] = useState<IContactFile>();
  const [stipReviewModalPosition, setStipReviewModalPosition] = useState(0);
  const [stipTypeMap, setStipTypeMap] = useState<IStipTypeMap>({});
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [alertProps, setAlertProps] = useState<{ title: string; subtitle: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorPopUp, setErrorPopUp] = useState(false);

  const params = queryParams();
  const { data: contact } = useSWR<NContact.Entity>(`/${SERVICES.CONTACT}/contacts/${params.id}`);
  const { data: coApplicant } = useSWR<ICoApplicant>(
    params.id &&
      contact?.coApplicants?.[0]?.id &&
      `/${SERVICES.CONTACT}/contacts/${params.id}/co-applicants/${contact.coApplicants[0].id}`
  );
  const { data: assetData } = useSWR<IAsset>(params.assetId && `/${SERVICES.ASSET}/assets/${params.assetId}`);
  const apiRef = useGridApiRef();

  const stipTypesOptions = useMemo(() => getOptions(filteredStipType(stipTypes)), [stipTypes]);
  const { users } = useUsers();

  const onButtonClick = (row: IContactFile) => {
    setContactStipData(row);
    setStipReviewModalPosition((prevState) => prevState + 1);
  };

  const handleClose = () => {
    setOpenAlertModal(false);
    setAlertType('');
  };

  const handleStipReview = useCallback(
    (stipId: string, confidenceScore: number | null) => {
      const route = withQueryParams(
        '/stips-review',
        { id: params.id, dealId: params.dealId, assetId: params.assetId, stipId: stipId, confidenceScore: confidenceScore },
        false
      );
      window.open(route, '_blank');
    },
    [params]
  );

  const handleSelect = useCallback(
    async (value: string, row: IContactFile) => {
      setLoading(true);
      if (!!dealId && !!assetId) {
        await requestUpdateStipType(row.contactStipId, row.contactId, value, dealId, assetId);

        setStipTypeMap({
          ...stipTypeMap,
          [row.contactStipId]: value,
        });
      } else {
        setErrorPopUp(true);
      }
      setLoading(false);
      reloadFiles();
    },
    [stipTypeMap, dealId, assetId]
  );

  const handleOnClick = useCallback(
    (row: IContactFile | null, action: string) => {
      row && setDeleteContactStipData(row);
      let props = { title: '', subtitle: '' };
      switch (action) {
        case ALERT_TYPES.DELETE:
          props = { title: 'Delete', subtitle: 'Are you sure you want to Delete this?' };
          break;
        case ALERT_TYPES.RESTORE:
          props = { title: 'Restore', subtitle: 'Are you sure you want to Restore this?' };
          break;
        case ALERT_TYPES.ADMIN_DELETE:
          props = {
            title: 'Delete Permanently',
            subtitle: 'Are you sure you want to delete this document PERMANENTLY?',
          };
          break;
        default:
          break;
      }
      setAlertProps(props);
      setAlertType(action);
      setOpenAlertModal(true);
    },
    [alertType]
  );

  const handleConfirm = useCallback(async () => {
    switch (alertType) {
      case ALERT_TYPES.DELETE:
        deleteContactStipData && await requestDeleteStipFiles(deleteContactStipData?.contactStipId, deleteContactStipData?.contactId, false);
        break;
      case ALERT_TYPES.RESTORE:
        deleteContactStipData && await requestRestoreStipFiles(deleteContactStipData?.contactStipId, deleteContactStipData?.contactId);
        break;
      case ALERT_TYPES.ADMIN_DELETE:
        deleteContactStipData && await requestDeleteStipFiles(deleteContactStipData?.contactStipId, deleteContactStipData?.contactId, true);
        break;
      default:
        break;
    }
    dispatch(setLastCommentsRefresh(new Date().toISOString()));
    reloadFiles();
    setOpenAlertModal(false);
    setAlertType('');
  }, [alertType, deleteContactStipData]);

  const downloadFile = useCallback(async (row: IContactFile) => {
    setLoading(true);
    const stipData: IStip = await requestGetStip(row.contactStipId, row.contactId);
    if (stipData?.content || stipData?.blobDownloadLink) {
      downloadAsData(stipData.blobFile.name, stipData.content, stipData?.blobDownloadLink);
    }
    setLoading(false);
  }, []);

  const getConfidenceScore = (row: IContactFile): number => {
    const isAssetLoading = Boolean(params.assetId && !assetData);
    const isCoApplicantLoading = Boolean(contact?.coApplicants?.length && !coApplicant);
    if (!contact || isCoApplicantLoading || isAssetLoading) return ZERO_CONFIDENCE_SCORE;
    const stipReview = normalizeContactFileToStipReview(row);
    if (!stipReview) return ZERO_CONFIDENCE_SCORE;
    const comparableFields = getComparableFieldsForContactFile(stipReview);
    if (!comparableFields?.length) return ZERO_CONFIDENCE_SCORE;

    const comparedStatuses = comparableFields.map(({ name, compareWith }) =>
      getFieldCompareStatus(
        typeof name === 'string' ? name : '',
        compareWith,
        stipReview,
        contact ?? null,
        assetData,
        coApplicant
      )
    );

    if (!comparedStatuses.length) return ZERO_CONFIDENCE_SCORE;
    const filteredStatuses = comparedStatuses.filter((status) => status !== 'none');

    const matchedCount = filteredStatuses.filter((status) => status === 'matched').length;
    const confidenceScore = filteredStatuses.length
      ? (matchedCount / filteredStatuses.length) * FULL_CONFIDENCE_SCORE
      : ZERO_CONFIDENCE_SCORE;

    return Math.round(confidenceScore);
  };

  const rowsWithConfidenceScores = useMemo(() => {
    if (!rows?.length || !contact) return rows ?? [];

    return rows.map((row) => {
      if (!row?.ocrResult) return row;

      return {
        ...row,
        ocrResult: {
          ...row.ocrResult,
          confidenceScore: getConfidenceScore(row),
        },
      };
    });
  }, [assetData, coApplicant, contact, rows]);

  const columns: GridColDef[] = [
    dataGridCol('confidenceScore', {
      headerName: 'Confidence Score',
      minWidth: 110,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        return (
          <ConfidenceScore
            score={row.ocrResult?.confidenceScore}
            onClick={() => handleStipReview(row.contactStipId, row.ocrResult?.confidenceScore)}
          />
        );
      },
    }),

    dataGridCol('uploadedOn', {
      type: 'dateTime',
      valueFormatter: gridDateTimeFormatter,
      minWidth: 170,
    }),
    dataGridCol('stipType', {
      minWidth: 200,
      headerName: 'Type',
      renderCell: ({ row }) => (
        <SelectAutocomplete
          value={stipTypeMap[row.contactStipId] || row.stipTypeId || ''}
          options={stipTypesOptions}
          disabled={row.isDeleted}
          canBeClean={false}
          onChange={(value: string) => handleSelect(value, row)}
        />
      ),
    }),
    dataGridCol('fileName', {
      minWidth: 350,
      flex: 2,
      renderCell: ({ value, row }) => (
        <Button
          disabled={row.isDeleted}
          onClick={() => onButtonClick(row)}
        >
          {value}
        </Button>
      ),
    }),
    dataGridCol('uploadedTo', {
      minWidth: 110,
    }),
    dataGridCol('uploadedBy', {
      minWidth: 160,
      valueGetter: ({ value }) => users?.groupUsers?.find((r: GroupUser) => r.userId === value?.id)?.userName ?? value?.name,
    }),
    dataGridCol('isDeleted', {
      hide: true,
    }),
    dataGridCol('actions', {
      minWidth: 120,
      renderCell: ({ row }) => (
        <>
          {row.isDeleted ? (
            <>
              {' '}
              <IconButton
                data-action="edit-row"
                title="Restore"
                onClick={() => handleOnClick(row, ALERT_TYPES.RESTORE)}
              >
                <RestoreFromTrash />
              </IconButton>
              <IconButton
                data-action="edit-row"
                title="Admin Delete"
                onClick={() => handleOnClick(row, ALERT_TYPES.ADMIN_DELETE)}
              >
                <DeleteForever />
              </IconButton>{' '}
            </>
          ) : (
            <>
              <IconButton
                data-action="edit-row"
                onClick={() => downloadFile(row)}
              >
                <DownloadIcon {...gridRowActionsIconProps} />
              </IconButton>
              <IconButton
                data-action="edit-row"
                title="Delete"
                onClick={() => handleOnClick(row, ALERT_TYPES.DELETE)}
              >
                <Delete />
              </IconButton>
            </>
          )}
        </>
      ),
    }),
  ];

  const handleCheckboxSelection = useCallback((model: GridSelectionModel) => {
    handleSelectedFiles(model);
  }, []);

  const onSortModalChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(defaultSort.concat(sortModel));
  }, []);

  return (
    <Box sx={{ height: 'auto' }}>
      <LoaderBackdrop
        open={loading}
        handleClose={() => setLoading(false)}
      />
      {openAlertModal && alertProps && (
        <AlertModal
          title={alertProps?.title}
          subtitle={alertProps?.subtitle}
          buttonText={alertType === 'pushStips' ? 'OK' : 'Confirm'}
          open={openAlertModal}
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      )}
      {errorPopUp && (
        <AlertModal
          open
          title="Alert!"
          subtitle="Please select DealId & AssetId"
          buttonText="Okay"
          hideCancel={true}
          onConfirm={() => setErrorPopUp(false)}
        />
      )}

      <PhoenixGrid
        withHeader
        canNotCopy
        canNotEdit
        noButton
        checkboxSelection
        disableSelectionOnClick
        autoHeight
        apiRef={apiRef}
        columns={columns}
        loading={isLoading}
        getRowId={(row) => row.contactStipId}
        rows={isLoading ? [] : [...rowsWithConfidenceScores].sort((x, y) => (x.isDeleted === y.isDeleted ? 0 : x.isDeleted ? 1 : -1))}
        selectionModel={selectedFiles}
        sortModel={sortModel}
        sx={{
          '& .MuiBadge-badge, .MuiBadge-standard': {
            visibility: 'hidden !important',
          },
        }}
        onSortModelChange={onSortModalChange}
        onSelectionModelChange={handleCheckboxSelection}
      />
      {contactStipData && (
        <StipReviewModal
          stipReviewModalPosition={stipReviewModalPosition}
          open={!!stipReviewModalPosition}
          contactStipData={contactStipData}
          confidenceScore={contactStipData.ocrResult?.confidenceScore}
          onClose={() => setStipReviewModalPosition(0)}
        />
      )}
    </Box>
  );
};
